<template>
  <component
    :is="props.href ? 'a' : 'div'"
    :href="props.href"
    :target="props.type === 'external' ? '_blank' : undefined"
    :rel="props.type === 'external' ? 'noopener noreferrer' : undefined"
    class="w-fit font-medium text-[14px] leading-[22px] cursor-pointer text-green-700 hover:text-green-600"
    :class="classes"
  >
    <Icon
      v-if="leftIcon"
      :size="iconSize"
      :name="leftIcon"
      class="inline-block align-middle mr-px"
      :class="iconClass"
    />

    {{ slotText.before }}

    <span class="whitespace-nowrap">
      {{ slotText.lastWord }}
      <Icon
        v-if="rightIcon"
        :size="iconSize"
        :name="rightIcon"
        class="inline-block align-middle ml-px"
        :class="iconClass"
      />
    </span>
  </component>
</template>

<script setup lang="ts">
import { computed, useSlots, type VNode } from 'vue'
import { Icon, type IconName } from '@dacr/ui'

export type LinkType =
  | 'primary'
  | 'secondary'
  | 'breadcrumb'
  | 'copy'
  | 'external'
  | 'left'

const props = withDefaults(
  defineProps<{
    type?: LinkType
    href?: string
  }>(),
  {
    type: 'primary',
  }
)

const slots = useSlots()

const slotText = computed(() => {
  const slotContent = slots.default?.()

  if (!slotContent?.length) {
    return {
      before: '',
      lastWord: '',
    }
  }

  const extractText = (nodes: VNode[]): string => {
    return nodes
      .map((node) => {
        if (typeof node.children === 'string') {
          return node.children
        }

        if (Array.isArray(node.children)) {
          return extractText(node.children as VNode[])
        }

        return ''
      })
      .join('')
  }

  const text = extractText(slotContent).trim()
  const lastSpaceIndex = text.lastIndexOf(' ')

  if (lastSpaceIndex === -1) {
    return { before: '', lastWord: text }
  }

  return {
    before: text.slice(0, lastSpaceIndex + 1),
    lastWord: text.slice(lastSpaceIndex + 1),
  }
})

const leftIcon = computed<IconName | null>(() => {
  switch (props.type) {
    case 'copy':
      return 'Link'
    case 'left':
      return 'LinkArrow'
    default:
      return null
  }
})

const rightIcon = computed<IconName | null>(() => {
  switch (props.type) {
    case 'primary':
    case 'secondary':
      return 'LinkArrow'
    case 'external':
      return 'External'
    case 'breadcrumb':
      return 'ChevronUp'
    default:
      return null
  }
})

const iconSize = computed(() => {
  return props.type === 'copy' ? 18 : 12
})

const classes = computed(() => {
  if (props.type === 'secondary') {
    return 'font-secondary'
  }

  return 'font-primary'
})

const iconClass = computed(() => {
  if (props.type === 'breadcrumb') {
    return 'stroke-gray-400 rotate-90'
  }

  if (props.type === 'left') {
    return 'rotate-180'
  }

  return 'stroke-green-700'
})
</script>
